import React, { useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { makeGetTask } from 'selectors/tasks';
import { makeGetMilestone } from 'selectors/milestones';

import { updateTask } from 'actions/tasks';
import { tieTasks, untieTasks, tieMilestones, untieMilestones } from 'actions/goals';

import GoalContainer from 'containers/GoalContainer';

import SelectGoalPopout from 'components/SelectGoalPopout';
import CanUser from 'components/can-user';
import Tooltip from 'components/tooltip';

import IconGoal from 'icons/icon-goal';
import IconBrokenLink from 'icons/icon-broken-link';

import './TiedGoals.styles.css';

const GoalEntry = ({ id, name, onClick, onUnlinkClick }) => {
    const { t } = useTranslation('goal');

    const handleClick = () => {
        onClick(id);
    }

    const handleUnlinkClick = () => {
        onUnlinkClick(id);
    }

    if (!name) {
        return null;
    }

    return (
        <div className="linked-task-entry">
            <div className="entry-body" onClick={handleClick}>
                { name }
            </div>

            <CanUser type="editGoal">
                <div className="entry-foot">
                    <Tooltip tooltip={ t('untieGoal.title') } withTooltipHover={false} placement="top">
                        <button className="btn btn-circle btn-xxsmall btn-transparent btn-icon" onClick={handleUnlinkClick}>
                            <IconBrokenLink />
                        </button>
                    </Tooltip>
                </div>
            </CanUser>
        </div>
    );
}

const TiedGoals = ({ milestoneId, taskId, projectId, goals, createdBy }) => {
    const { t } = useTranslation(['goal', 'task']);
    const history = useHistory();
    const handleTieToGoalSelect = useCallback(({ id: goalId }) => {
        if (taskId) {
            tieTasks(goalId, [taskId]);
        } else {
            tieMilestones(goalId, [milestoneId]);
        }
    }, [taskId, milestoneId]);

    const handleGoalUnlinkClick = useCallback(goalId => {
        if (taskId) {
            untieTasks(goalId, [taskId]);
        } else {
            untieMilestones(goalId, [milestoneId]);
        }
    }, [taskId, milestoneId]);

    const handleGoalClick = useCallback(goalId => {
        history.push(`/goals/${goalId}`);
    }, []);

    return (
        <div className="content-panel-block">
            <div className="content-panel-section">
                <div className="section-head">
                    <h4>
                        <span className="title-icon"><IconGoal /></span>

                        <span className="text">{ t('tiedGoals') }</span>

                        <CanUser type={taskId ? 'editTask' : 'editMilestone'} customList={createdBy}>
                            <SelectGoalPopout
                                title={ t('task:actions.tieToGoal.title') }
                                excluded={goals}
                                xAlign="right"
                                defaultXPosition="left"
                                onSelect={handleTieToGoalSelect}
                            >
                                <button className="btn btn-circle btn-xxsmall btn-simple btn-icon btn-colored">
                                    +
                                </button>
                            </SelectGoalPopout>
                        </CanUser>
                    </h4>
                </div>

                <div className="section-body">
                    {
                        goals.map(goalId => (
                            <GoalContainer key={goalId} id={goalId}>
                                <GoalEntry
                                    onClick={handleGoalClick}
                                    onUnlinkClick={handleGoalUnlinkClick}
                                />
                            </GoalContainer>
                        ))
                    }
                </div>
            </div>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();

    return ({ tasks, milestones }, ownProps) => {
        const task = getTask(tasks.items, ownProps.taskId);
        const milestone = getMilestone(milestones.items, ownProps.milestoneId);

        return {
            projectId: task?.project || milestone?.project,
            goals: task?.goals || milestone?.goals,
            createdBy: task?.created_by || milestone?.created_by
        }
    };
}

export default connect(makeMapStateToProps)(TiedGoals);
